import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    QrCode, CheckCircle, XCircle, UserPlus, House, Camera, SignIn, SignOut,
    Car, Users, ClockCounterClockwise, ShieldWarning, Trash, UserCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { fileDownloadUrl } from "@/lib/files";

const TABS = [
    { key: "scan", label: "Scan", icon: QrCode },
    { key: "manual", label: "Manual", icon: UserPlus },
    { key: "inside", label: "Inside", icon: Users },
    { key: "vehicle", label: "Vehicle", icon: Car },
    { key: "activity", label: "Activity", icon: ClockCounterClockwise },
];

export default function QRScanner() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("scan");
    const [stats, setStats] = useState(null);

    useEffect(() => {
        loadStats();
        const t = setInterval(loadStats, 30000);
        return () => clearInterval(t);
    }, []);

    async function loadStats() {
        try {
            const { data } = await api.get("/gate/security/home");
            setStats(data);
        } catch (e) { /* best-effort */ }
    }

    return (
        <div className="min-h-screen bg-brand-bg flex flex-col">
            <div className="bg-white border-b border-brand-line px-4 py-3 sticky top-0 z-20">
                <div className="max-w-4xl mx-auto flex items-center justify-between">
                    <Logo size={44} />
                    <div className="text-right">
                        <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Gate Security</p>
                        <p className="font-heading font-semibold text-brand-ink text-sm">{user?.society_name || "maintayn"}</p>
                    </div>
                    <Button data-testid="security-logout-btn" variant="outline" size="sm" onClick={async () => { await logout(); navigate("/gate-security"); }} className="rounded-full ml-3">
                        <SignOut size={14} className="mr-1.5" /> Sign out
                    </Button>
                </div>
            </div>

            {stats && (
                <div className="max-w-4xl mx-auto w-full px-4 pt-3 grid grid-cols-4 gap-2">
                    <StatPill testId="stat-visitors-inside" label="Visitors In" value={stats.visitors_inside} />
                    <StatPill testId="stat-vehicles-inside" label="Vehicles In" value={stats.vehicles_inside} />
                    <StatPill testId="stat-today-entries" label="Entries Today" value={stats.today_entries} />
                    <StatPill testId="stat-pending-approvals" label="Pending" value={stats.pending_visitor_approvals} accent />
                </div>
            )}

            <div className="max-w-4xl mx-auto w-full px-4 pb-24 pt-4 flex-1">
                {tab === "scan" && <ScanTab onDone={loadStats} />}
                {tab === "manual" && <ManualVisitorTab onDone={loadStats} />}
                {tab === "inside" && <VisitorsInsideTab onDone={loadStats} />}
                {tab === "vehicle" && <VehicleEntryTab onDone={loadStats} />}
                {tab === "activity" && <ActivityTab />}
            </div>

            {/* Bottom tab bar — mobile-first, touch-friendly */}
            <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-brand-line z-20">
                <div className="max-w-4xl mx-auto grid grid-cols-5">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        const active = tab === t.key;
                        return (
                            <button
                                key={t.key}
                                data-testid={`security-tab-${t.key}`}
                                onClick={() => setTab(t.key)}
                                className={`flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition-colors ${
                                    active ? "text-brand-action" : "text-brand-inkSoft hover:text-brand-ink"
                                }`}
                            >
                                <Icon size={22} weight={active ? "fill" : "regular"} />
                                {t.label}
                            </button>
                        );
                    })}
                </div>
            </nav>
        </div>
    );
}

function StatPill({ label, value, accent, testId }) {
    return (
        <div data-testid={testId} className={`rounded-sm p-2.5 text-center border ${accent ? "bg-brand-action/10 border-brand-action/30" : "bg-white border-brand-line"}`}>
            <p className={`font-heading text-xl font-bold ${accent ? "text-brand-action" : "text-brand-ink"}`}>{value ?? "—"}</p>
            <p className="text-[9px] uppercase tracking-overline text-brand-inkSoft mt-0.5">{label}</p>
        </div>
    );
}

// ---------------------------------------------------------------- Scan Tab ----
function ScanTab({ onDone }) {
    const [scanning, setScanning] = useState(false);
    const [scanner, setScanner] = useState(null);
    const [lastScan, setLastScan] = useState(null);
    const [result, setResult] = useState(null);
    const [cameraError, setCameraError] = useState(null);
    const [acting, setActing] = useState(false);

    useEffect(() => () => {
        if (scanner) scanner.stop().then(() => scanner.clear()).catch(() => {});
    }, [scanner]);

    async function startScanning() {
        setCameraError(null);
        setScanning(true);
        try {
            if (!navigator.mediaDevices?.getUserMedia) throw new Error("Camera access is not supported on this device/browser");
            let stream;
            try {
                stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } } });
            } catch (permErr) {
                const msg = permErr.name === "NotFoundError" ? "No camera found on this device." : "Camera permission denied. Please enable camera access.";
                setCameraError(msg);
                toast.error(msg);
                setScanning(false);
                return;
            }
            stream.getTracks().forEach((t) => t.stop());
            const html5QrCode = new Html5Qrcode("qr-reader");
            setScanner(html5QrCode);
            setResult(null);
            await html5QrCode.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1.0 }, onScanSuccess, () => {});
        } catch (err) {
            const msg = err.message || "Unable to access camera";
            setCameraError(msg);
            toast.error(msg);
            setScanning(false);
            setScanner(null);
        }
    }

    async function stopScanning() {
        if (scanner) {
            try { await scanner.stop(); await scanner.clear(); } catch (e) { /* noop */ }
            setScanning(false);
            setScanner(null);
        }
    }

    async function onScanSuccess(decodedText) {
        if (decodedText === lastScan) return;
        setLastScan(decodedText);
        await stopScanning();
        try {
            const { data } = await api.post("/gate/verify", { qr_payload: decodedText });
            setResult(data);
        } catch (e) {
            setResult({ valid: false, denial_reason: formatError(e), entity: null, actions: [] });
        }
    }

    function reset() {
        setResult(null);
        setLastScan(null);
    }

    async function runAction(action) {
        if (!result) return;
        setActing(true);
        try {
            if (result.pass_type === "VISITOR_PASS") {
                if (action === "CHECKIN") await api.post(`/visitors/${result.entity.visitor_id}/checkin`);
                if (action === "CHECKOUT") await api.post(`/visitors/${result.entity.visitor_id}/checkout`);
            } else if (result.pass_type === "VEHICLE_PASS") {
                if (action === "ENTRY") {
                    await api.post("/vehicle-logs", { vehicle_id: result.entity.vehicle_id, vehicle_number: result.entity.vehicle_number });
                } else if (action === "EXIT") {
                    const { data: logs } = await api.get(`/vehicle-logs?vehicle_id=${result.entity.vehicle_id}`);
                    const active = (logs || []).find((l) => l.entry_time && !l.exit_time);
                    if (active) await api.post(`/vehicle-logs/${active.id}/exit`);
                }
            }
            toast.success(action === "CHECKOUT" || action === "EXIT" ? "Exit logged" : "Entry logged");
            onDone?.();
            reset();
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setActing(false);
        }
    }

    return (
        <div className="animate-fade-up">
            <div className="bg-white border border-brand-line rounded-sm p-6 text-center">
                {!scanning ? (
                    <div className="space-y-4">
                        <div className="w-20 h-20 mx-auto bg-brand-sage rounded-full flex items-center justify-center">
                            <Camera size={40} className="text-brand-ink" />
                        </div>
                        <p className="text-brand-inkSoft text-sm">Scan any Maintayn pass — visitor, vehicle or parking</p>
                        {cameraError && (
                            <div className="bg-red-50 border border-red-200 rounded-sm p-3 text-sm text-red-800">{cameraError}</div>
                        )}
                        <Button data-testid="start-scan-btn" onClick={startScanning} className="w-full rounded-full bg-brand-action hover:bg-brand-actionHover text-white h-12 text-base">
                            <Camera size={18} className="mr-2" /> Start Camera & Scan
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div id="qr-reader" className="mx-auto"></div>
                        <Button data-testid="stop-scan-btn" onClick={stopScanning} variant="outline" className="w-full rounded-full h-12">
                            Stop Scanning
                        </Button>
                    </div>
                )}
            </div>

            {result && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in" data-testid="scan-result-modal">
                    <div className="w-full max-w-md rounded-lg shadow-2xl overflow-hidden bg-white">
                        <div className={`p-6 ${result.valid ? "bg-green-600" : "bg-red-600"}`}>
                            <div className="flex justify-center mb-2">
                                {result.valid ? <CheckCircle size={56} className="text-white" weight="fill" /> : <XCircle size={56} className="text-white" weight="fill" />}
                            </div>
                            <h2 className="font-heading text-2xl font-bold text-white text-center">
                                {result.valid ? "Pass Valid" : (result.status === "BLACKLISTED" ? "BLACKLISTED" : "Access Denied")}
                            </h2>
                            {result.pass_type && <p className="text-white/80 text-center text-xs uppercase tracking-overline mt-1">{result.pass_type.replace("_", " ")}</p>}
                        </div>

                        <div className="p-6 space-y-3">
                            {!result.valid && (
                                <p className="text-red-700 font-medium text-center" data-testid="scan-denial-reason">{result.denial_reason}</p>
                            )}
                            {result.entity && (
                                <div className="bg-brand-bg rounded-sm p-4 space-y-1.5 text-sm">
                                    {result.entity.name && <Row label="Name" value={result.entity.name} />}
                                    {result.entity.vehicle_number && <Row label="Vehicle" value={result.entity.vehicle_number} mono />}
                                    {result.entity.owner_name && <Row label="Owner" value={result.entity.owner_name} />}
                                    {result.entity.flat && <Row label="Flat" value={result.entity.flat} />}
                                    {result.entity.purpose && <Row label="Purpose" value={result.entity.purpose} />}
                                    {result.entity.pass_number && <Row label="Pass #" value={result.entity.pass_number} mono />}
                                </div>
                            )}

                            <div className="flex flex-col gap-2 pt-2">
                                {result.actions?.includes("CHECKIN") && (
                                    <Button data-testid="scan-action-checkin" disabled={acting} onClick={() => runAction("CHECKIN")} className="rounded-full bg-green-600 hover:bg-green-700 text-white h-11">
                                        <SignIn size={16} className="mr-2" /> Check In
                                    </Button>
                                )}
                                {result.actions?.includes("CHECKOUT") && (
                                    <Button data-testid="scan-action-checkout" disabled={acting} onClick={() => runAction("CHECKOUT")} className="rounded-full bg-blue-600 hover:bg-blue-700 text-white h-11">
                                        <SignOut size={16} className="mr-2" /> Check Out
                                    </Button>
                                )}
                                {result.actions?.includes("ENTRY") && (
                                    <Button data-testid="scan-action-entry" disabled={acting} onClick={() => runAction("ENTRY")} className="rounded-full bg-green-600 hover:bg-green-700 text-white h-11">
                                        <SignIn size={16} className="mr-2" /> Log Entry
                                    </Button>
                                )}
                                {result.actions?.includes("EXIT") && (
                                    <Button data-testid="scan-action-exit" disabled={acting} onClick={() => runAction("EXIT")} className="rounded-full bg-blue-600 hover:bg-blue-700 text-white h-11">
                                        <SignOut size={16} className="mr-2" /> Log Exit
                                    </Button>
                                )}
                                <Button data-testid="scan-close-btn" variant="outline" onClick={reset} className="rounded-full h-11">
                                    {result.actions?.length ? "Cancel" : "Close"}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Row({ label, value, mono }) {
    return (
        <div className="flex justify-between gap-3">
            <span className="text-brand-inkSoft">{label}</span>
            <span className={`text-brand-ink font-medium text-right ${mono ? "font-mono" : ""}`}>{value}</span>
        </div>
    );
}

// -------------------------------------------------------- Manual Visitor Tab ----
function ManualVisitorTab({ onDone }) {
    const [flats, setFlats] = useState([]);
    const [categories, setCategories] = useState([]);
    const [form, setForm] = useState({ name: "", phone: "", purpose: "", flat_id: "", vehicle_no: "", category: "delivery", company: "", photo_file_id: "" });
    const [submitting, setSubmitting] = useState(false);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const photoInputRef = React.useRef();

    useEffect(() => {
        api.get("/flats").then(({ data }) => setFlats(data || [])).catch(() => {});
        api.get("/gate/meta").then(({ data }) => setCategories(data.visitor_categories || [])).catch(() => {});
    }, []);

    async function capturePhoto(file) {
        if (!file) return;
        setUploadingPhoto(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setForm((f) => ({ ...f, photo_file_id: data.id }));
            toast.success("Photo captured");
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setUploadingPhoto(false);
        }
    }

    async function submit(e) {
        e.preventDefault();
        setSubmitting(true);
        try {
            const res = await api.post("/visitors/manual-entry", form);
            toast.success(`Entry request sent for ${res.data.name} — awaiting resident approval`);
            setForm({ name: "", phone: "", purpose: "", flat_id: "", vehicle_no: "", category: "delivery", company: "", photo_file_id: "" });
            onDone?.();
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setSubmitting(false);
        }
    }

    const photoUrl = fileDownloadUrl(form.photo_file_id);

    return (
        <div className="bg-white border border-brand-line rounded-sm p-5 animate-fade-up">
            <div className="flex items-center gap-2 mb-4">
                <UserPlus size={22} className="text-brand-ink" />
                <h2 className="font-heading text-lg text-brand-ink">Manual Visitor Entry</h2>
            </div>
            <form onSubmit={submit} className="space-y-3">
                <div className="space-y-1.5">
                    <Label className="text-xs">Visitor Photo (optional)</Label>
                    <input ref={photoInputRef} type="file" accept="image/*" capture="environment" className="hidden"
                        onChange={(e) => e.target.files?.[0] && capturePhoto(e.target.files[0])} />
                    {photoUrl ? (
                        <div className="flex items-center gap-3">
                            <img src={photoUrl} alt="visitor" data-testid="manual-visitor-photo-preview" className="w-16 h-16 rounded-sm object-cover border border-brand-line" />
                            <Button type="button" variant="outline" size="sm" data-testid="manual-visitor-retake-photo-btn"
                                onClick={() => photoInputRef.current?.click()} disabled={uploadingPhoto} className="rounded-full text-xs">
                                <Camera size={13} className="mr-1.5" /> Retake
                            </Button>
                            <button type="button" data-testid="manual-visitor-remove-photo-btn"
                                onClick={() => setForm((f) => ({ ...f, photo_file_id: "" }))}
                                className="text-brand-inkSoft hover:text-red-600">
                                <Trash size={16} />
                            </button>
                        </div>
                    ) : (
                        <Button type="button" variant="outline" data-testid="manual-visitor-take-photo-btn"
                            onClick={() => photoInputRef.current?.click()} disabled={uploadingPhoto} className="w-full rounded-full text-sm h-10 border-dashed">
                            <Camera size={15} className="mr-2" /> {uploadingPhoto ? "Uploading…" : "Take Photo"}
                        </Button>
                    )}
                </div>
                <div className="space-y-1.5">
                    <Label className="text-xs">Visitor Name *</Label>
                    <Input required data-testid="manual-visitor-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm border-brand-line" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <Label className="text-xs">Phone</Label>
                        <Input data-testid="manual-visitor-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm border-brand-line" />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs">Category</Label>
                        <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                            <SelectTrigger data-testid="manual-visitor-category-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                            <SelectContent>{categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="space-y-1.5">
                    <Label className="text-xs">Purpose *</Label>
                    <Input required data-testid="manual-visitor-purpose-input" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="Delivery, guest, service..." className="rounded-sm border-brand-line" />
                </div>
                <div className="space-y-1.5">
                    <Label className="text-xs">Visiting Flat *</Label>
                    <Select value={form.flat_id} onValueChange={(v) => setForm({ ...form, flat_id: v })}>
                        <SelectTrigger data-testid="manual-visitor-flat-select" className="rounded-sm border-brand-line"><SelectValue placeholder="Select flat" /></SelectTrigger>
                        <SelectContent>{flats.map((f) => <SelectItem key={f.id} value={f.id}>{f.block}-{f.number}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <Label className="text-xs">Vehicle No.</Label>
                        <Input data-testid="manual-visitor-vehicle-input" value={form.vehicle_no} onChange={(e) => setForm({ ...form, vehicle_no: e.target.value.toUpperCase() })} className="rounded-sm border-brand-line" />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs">Company</Label>
                        <Input data-testid="manual-visitor-company-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="rounded-sm border-brand-line" />
                    </div>
                </div>
                <Button type="submit" disabled={submitting || !form.flat_id} data-testid="manual-visitor-submit-btn" className="w-full rounded-full bg-brand-action hover:bg-brand-actionHover text-white h-11">
                    {submitting ? "Sending…" : "Send Entry Request"}
                </Button>
            </form>
        </div>
    );
}

// --------------------------------------------------------- Vehicle Entry Tab ----
function VehicleEntryTab({ onDone }) {
    const [flats, setFlats] = useState([]);
    const [form, setForm] = useState({ vehicle_number: "", flat_id: "", driver_name: "", purpose: "", vehicle_type: "", notes: "" });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { api.get("/flats").then(({ data }) => setFlats(data || [])).catch(() => {}); }, []);

    async function submit(e) {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = { ...form, flat_id: form.flat_id || null };
            await api.post("/gate/vehicles/manual-entry", payload);
            toast.success(`Entry logged for ${form.vehicle_number.toUpperCase()}`);
            setForm({ vehicle_number: "", flat_id: "", driver_name: "", purpose: "", vehicle_type: "", notes: "" });
            onDone?.();
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="bg-white border border-brand-line rounded-sm p-5 animate-fade-up">
            <div className="flex items-center gap-2 mb-4">
                <Car size={22} className="text-brand-ink" />
                <h2 className="font-heading text-lg text-brand-ink">Manual Vehicle Entry</h2>
            </div>
            <form onSubmit={submit} className="space-y-3">
                <div className="space-y-1.5">
                    <Label className="text-xs">Vehicle Number *</Label>
                    <Input required data-testid="manual-vehicle-number-input" value={form.vehicle_number} onChange={(e) => setForm({ ...form, vehicle_number: e.target.value.toUpperCase() })} placeholder="MH12AB1234" className="rounded-sm border-brand-line uppercase font-mono" />
                </div>
                <div className="space-y-1.5">
                    <Label className="text-xs">Visiting Flat (optional)</Label>
                    <Select value={form.flat_id} onValueChange={(v) => setForm({ ...form, flat_id: v })}>
                        <SelectTrigger data-testid="manual-vehicle-flat-select" className="rounded-sm border-brand-line"><SelectValue placeholder="Select flat" /></SelectTrigger>
                        <SelectContent>{flats.map((f) => <SelectItem key={f.id} value={f.id}>{f.block}-{f.number}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <Label className="text-xs">Driver Name</Label>
                        <Input data-testid="manual-vehicle-driver-input" value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} className="rounded-sm border-brand-line" />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs">Purpose</Label>
                        <Input data-testid="manual-vehicle-purpose-input" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="Cab, delivery…" className="rounded-sm border-brand-line" />
                    </div>
                </div>
                <div className="space-y-1.5">
                    <Label className="text-xs">Notes</Label>
                    <Textarea rows={2} data-testid="manual-vehicle-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm border-brand-line" />
                </div>
                <div className="bg-brand-sage/20 rounded-sm p-3 text-xs text-brand-inkSoft flex items-start gap-2">
                    <ShieldWarning size={16} className="shrink-0 mt-0.5" />
                    Blacklisted or unapproved registered vehicles are automatically denied entry, even here.
                </div>
                <Button type="submit" disabled={submitting} data-testid="manual-vehicle-submit-btn" className="w-full rounded-full bg-brand-action hover:bg-brand-actionHover text-white h-11">
                    {submitting ? "Logging…" : "Log Vehicle Entry"}
                </Button>
            </form>
        </div>
    );
}

// -------------------------------------------------------- Visitors Inside Tab ----
function VisitorsInsideTab({ onDone }) {
    const [visitors, setVisitors] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/gate/security/visitors-inside");
            setVisitors(data || []);
        } catch (e) { toast.error(formatError(e)); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    async function checkout(v) {
        try {
            await api.post(`/visitors/${v.id}/checkout`);
            toast.success(`${v.name} checked out`);
            load();
            onDone?.();
        } catch (e) { toast.error(formatError(e)); }
    }

    if (loading) return <p className="text-center text-brand-inkSoft py-10">Loading…</p>;

    return (
        <div className="animate-fade-up space-y-3">
            <h2 className="font-heading text-lg text-brand-ink flex items-center gap-2">
                <Users size={20} /> Visitors Inside ({visitors.length})
            </h2>
            {visitors.length === 0 ? (
                <div className="bg-white border border-brand-line rounded-sm p-8 text-center text-brand-inkSoft">No visitors currently inside</div>
            ) : (
                visitors.map((v) => (
                    <div key={v.id} data-testid={`inside-visitor-${v.id}`} className="bg-white border border-brand-line rounded-sm p-4 flex items-center justify-between">
                        <div>
                            <p className="font-medium text-brand-ink">{v.name}</p>
                            <p className="text-xs text-brand-inkSoft flex items-center gap-1 mt-0.5">
                                <House size={12} /> {v.flat ? `${v.flat.block}-${v.flat.number}` : "—"} · {v.purpose}
                            </p>
                        </div>
                        <Button size="sm" data-testid={`inside-checkout-btn-${v.id}`} onClick={() => checkout(v)} className="rounded-full bg-blue-600 hover:bg-blue-700 text-white text-xs h-8">
                            <SignOut size={13} className="mr-1" /> Check Out
                        </Button>
                    </div>
                ))
            )}
        </div>
    );
}

// -------------------------------------------------------------- Activity Tab ----
function ActivityTab() {
    const [feed, setFeed] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/gate/security/today-activity").then(({ data }) => setFeed(data || [])).catch((e) => toast.error(formatError(e))).finally(() => setLoading(false));
    }, []);

    const iconFor = (type) => {
        if (type === "VISITOR_ENTRY") return <SignIn size={16} className="text-green-600" />;
        if (type === "VISITOR_EXIT") return <SignOut size={16} className="text-blue-600" />;
        if (type === "VEHICLE_ENTRY") return <Car size={16} className="text-green-600" />;
        return <Car size={16} className="text-blue-600" />;
    };

    if (loading) return <p className="text-center text-brand-inkSoft py-10">Loading…</p>;

    return (
        <div className="animate-fade-up space-y-3">
            <h2 className="font-heading text-lg text-brand-ink flex items-center gap-2">
                <ClockCounterClockwise size={20} /> Today's Activity ({feed.length})
            </h2>
            {feed.length === 0 ? (
                <div className="bg-white border border-brand-line rounded-sm p-8 text-center text-brand-inkSoft">No activity yet today</div>
            ) : (
                <div className="bg-white border border-brand-line rounded-sm divide-y divide-brand-line">
                    {feed.map((f, i) => (
                        <div key={i} data-testid={`activity-row-${i}`} className="p-3 flex items-center gap-3">
                            {iconFor(f.type)}
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-brand-ink truncate">{f.label || "—"}</p>
                                <p className="text-xs text-brand-inkSoft truncate">{f.detail || f.type.replace("_", " ")}</p>
                            </div>
                            <p className="text-xs text-brand-inkSoft shrink-0">{new Date(f.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
